import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Plus, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingPost, setAddingPost] = useState(false);
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    excerpt: "",
    image_url: "",
    author: "PFCA Team",
    tags: []
  });
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput && !newPost.tags.includes(tagInput)) {
      setNewPost({ ...newPost, tags: [...newPost.tags, tagInput] });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setNewPost({ ...newPost, tags: newPost.tags.filter(t => t !== tag) });
  };

  const handleAddPost = async (e) => {
    e.preventDefault();
    if (!newPost.title || !newPost.content || !newPost.excerpt) {
      toast.error("Please fill in required fields");
      return;
    }

    setAddingPost(true);
    try {
      const response = await axios.post(`${API}/blog`, newPost);
      setPosts([response.data, ...posts]);
      setNewPost({
        title: "",
        content: "",
        excerpt: "",
        image_url: "",
        author: "PFCA Team",
        tags: []
      });
      setAddDialogOpen(false);
      toast.success("Post published successfully!");
    } catch (error) {
      console.error("Error adding post:", error);
      toast.error("Failed to publish post");
    } finally {
      setAddingPost(false);
    }
  };

  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                News & Updates
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
                Our Blog
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl">
                Stay updated with the latest news, stories, and insights from Peace for Change Alliance. Learn about our programs, events, and community impact.
              </p>
            </div>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-post-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Create New Blog Post</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddPost} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="post-title">Title *</Label>
                    <Input
                      id="post-title"
                      value={newPost.title}
                      onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                      placeholder="Enter post title"
                      className="mt-1"
                      required
                      data-testid="post-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="post-excerpt">Excerpt *</Label>
                    <Textarea
                      id="post-excerpt"
                      value={newPost.excerpt}
                      onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })}
                      placeholder="Brief summary of the post"
                      className="mt-1 resize-none"
                      rows={2}
                      required
                      data-testid="post-excerpt-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="post-content">Content *</Label>
                    <Textarea
                      id="post-content"
                      value={newPost.content}
                      onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                      placeholder="Write your post content..."
                      className="mt-1 resize-none"
                      rows={8}
                      required
                      data-testid="post-content-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="post-author">Author</Label>
                      <Input
                        id="post-author"
                        value={newPost.author}
                        onChange={(e) => setNewPost({ ...newPost, author: e.target.value })}
                        className="mt-1"
                        data-testid="post-author-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="post-image">Image URL</Label>
                      <Input
                        id="post-image"
                        value={newPost.image_url}
                        onChange={(e) => setNewPost({ ...newPost, image_url: e.target.value })}
                        placeholder="https://..."
                        className="mt-1"
                        data-testid="post-image-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Tags</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        placeholder="Add tag"
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                        data-testid="post-tag-input"
                      />
                      <Button type="button" variant="outline" onClick={handleAddTag}>
                        Add
                      </Button>
                    </div>
                    {newPost.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {newPost.tags.map(tag => (
                          <span 
                            key={tag} 
                            className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm flex items-center gap-1"
                          >
                            {tag}
                            <button 
                              type="button" 
                              onClick={() => handleRemoveTag(tag)}
                              className="hover:text-red-500"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={addingPost}
                    data-testid="submit-post-btn"
                  >
                    {addingPost ? "Publishing..." : "Publish Post"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner w-10 h-10" />
            </div>
          ) : posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <article 
                  key={post.id} 
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 group"
                  data-testid={`blog-card-${post.id}`}
                >
                  {post.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.image_url} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(post.created_at), "MMM d, yyyy")}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {post.author}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-[#0F172A] mb-2 line-clamp-2 group-hover:text-[#0056D2] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    {post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map(tag => (
                          <span 
                            key={tag} 
                            className="bg-[#E0F2FE] text-[#0056D2] px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                          >
                            <Tag className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <Link to={`/blog/${post.id}`}>
                      <Button variant="ghost" className="p-0 h-auto text-[#0056D2] hover:text-[#0044A5] font-medium">
                        Read More
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#F0F9FF] rounded-3xl">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Posts Yet</h3>
              <p className="text-slate-500 mb-6">Start sharing your stories and news.</p>
              <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
                <Plus className="w-5 h-5 mr-2" />
                Create First Post
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
